import {first} from 'lodash';
import React, {FC, ForwardedRef, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {LoadedMessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {preventDragStart} from '../../../../../helpers/dragAndDropHelpers';
import {AnnotationsHighlightViewerInheritableProps} from '../../../../common/annotation/annotationsHighlightViewer';
import {MessageViewerAttachmentsIcon} from './messageViewerAttachmentsIcon';
import {MessageViewerAvatar} from './messageViewerAvatar';
import {MessageViewerDate, MessageViewerDateProps} from './messageViewerDate';
import {MessageViewerDetails, MessageViewerDetailsProps} from './messageViewerDetails';
import {MessageViewerHeaderReplyContainer} from './messageViewerHeaderReplyContainer';
import {MessageViewerMoreBaseProps} from './messageViewerMore';
import {MessageViewerMoreContainer} from './messageViewerMoreContainer';
import {MessageViewerSeen} from './messageViewerSeen';

/*
 * Props.
 */

export interface MessageViewerFullHeaderProps
  extends MessageViewerDetailsProps,
    MessageViewerMoreBaseProps,
    Omit<MessageViewerDateProps, 'message'>,
    Pick<AnnotationsHighlightViewerInheritableProps, 'annotations' | 'hasMessageAnnotations'> {
  message: LoadedMessageModel;
  onCollapseClick: () => void;
  onPopoutClick: () => void;
  scrollerVisibilityRef?: ForwardedRef<HTMLDivElement>;
}

/*
 * Style.
 */

const StyledHeaderButton = styled(ChromelessButton)`
  display: grid;
  align-items: center;
  grid-template-columns: auto auto 1fr auto auto auto auto auto;
  grid-template-rows: auto auto;
  grid-template-areas:
    'avatar content . seen attachments date toolbar more'
    '.      content . .    .           .    .       .';
  padding: 8px 9px;
  border-bottom: 1px solid ${(p) => p.theme.message.border};
`;

const StyledToolbarDiv = styled.div`
  grid-area: toolbar;
  display: flex;
  margin-left: 2px;
`;

const StyledWrapperDiv = styled.div`
  grid-area: seen;
  margin: 0 7px;
`;

/*
 * Component.
 */

export const MessageViewerFullHeader: FC<PropsWithChildren<MessageViewerFullHeaderProps>> = (props) => (
  <StyledHeaderButton
    onClick={(e) => {
      if (e.defaultPrevented) {
        return;
      }
      props.onCollapseClick();
    }}
  >
    <MessageViewerAvatar
      panelId={props.panelId}
      message={props.message}
      author={props.author || first(props.visualRecipients.from)}
    />
    <MessageViewerDetails {...props} searchQuery={props.cellsContext.searchQuery} />
    {renderSeenOrSharedBy(props)}
    <MessageViewerAttachmentsIcon attachments={props.message.attachments} />
    <MessageViewerDate
      message={props.message}
      isInDragRegion={props.isInDragRegion}
      timelineVariation={props.timelineVariation}
    />
    <StyledToolbarDiv>
      <MessageViewerMoreContainer {...props} />
      {props.children}
      <MessageViewerHeaderReplyContainer
        message={props.message}
        selectedInboxId={props.cellsContext.inboxId}
        namespace={props.namespace}
        onReplyClick={props.onReplyClick}
        onReplyAllClick={props.onReplyAllClick}
        visualRecipients={props.visualRecipients}
      />
    </StyledToolbarDiv>
  </StyledHeaderButton>
);

function renderSeenOrSharedBy(props: MessageViewerFullHeaderProps) {
  return (
    <StyledWrapperDiv draggable={props.isInDragRegion} onDragStart={preventDragStart}>
      <MessageViewerSeen message={props.message} />
    </StyledWrapperDiv>
  );
}
